// Reading and writing a `*_i18n` name bundle in every product locale (D-430).
//
// Several office editors kept their own `["en", "ru", "zh", "fr"]` list, so a
// sixth language never reached them, and readers picked a name by cutting the
// UI language down to its first two letters, which reads Traditional Chinese
// as Simplified. Both now come from the registry: the editors list
// SUPPORTED_LOCALES, and the reader maps its language through
// `normalizeLocale`, which never truncates Chinese.
//
// This file holds stored name bundles only. The UI language and the
// endonyms a label prints (`LOCALE_ENDONYMS`) belong to `./locale.ts`, track
// L3's file, and are imported from there rather than kept twice.

import {
  normalizeLocale,
  SOURCE_LOCALE,
  SUPPORTED_LOCALES,
  type Locale,
} from "@assessly/contracts/locales";

/** A draft with one field per product locale, every field empty. */
export function emptyLocaleDraft(): Record<Locale, string> {
  return Object.fromEntries(SUPPORTED_LOCALES.map((l) => [l, ""])) as Record<Locale, string>;
}

/** A draft filled from a stored bundle; a locale it lacks is empty. */
export function localeDraftOf(
  bundle: Readonly<Partial<Record<string, string | null | undefined>>> | null | undefined,
): Record<Locale, string> {
  const draft = emptyLocaleDraft();
  for (const locale of SUPPORTED_LOCALES) draft[locale] = bundle?.[locale] ?? "";
  return draft;
}

/**
 * The bundle a write sends: the source locale always, every other locale only
 * when it holds text. Blanks are omitted, never sent as "".
 */
export function localeBundleOf(
  draft: Readonly<Record<Locale, string>>,
): { en: string } & Partial<Record<Locale, string>> {
  const bundle: { en: string } & Partial<Record<Locale, string>> = {
    en: draft[SOURCE_LOCALE].trim(),
  };
  for (const locale of SUPPORTED_LOCALES) {
    if (locale === SOURCE_LOCALE) continue;
    const text = draft[locale].trim();
    if (text.length > 0) bundle[locale] = text;
  }
  return bundle;
}

/** The locale a UI language tag stands for, or the source locale. */
export function uiLocale(language: string): Locale {
  return normalizeLocale(language) ?? SOURCE_LOCALE;
}

/**
 * The text a reader in `language` sees: their own locale's, then the source
 * locale's, then any non-empty one.
 */
export function bundleText(
  bundle: Readonly<Partial<Record<string, string | null | undefined>>> | null | undefined,
  language: string,
): string {
  if (bundle === null || bundle === undefined) return "";
  const own = bundle[uiLocale(language)];
  if (own !== null && own !== undefined && own.length > 0) return own;
  const source = bundle[SOURCE_LOCALE];
  if (source !== null && source !== undefined && source.length > 0) return source;
  for (const value of Object.values(bundle)) {
    if (value !== null && value !== undefined && value.length > 0) return value;
  }
  return "";
}

/**
 * The locales an editor shows up front: the source locale and the reader's
 * own. The rest sit behind an "Other languages" disclosure.
 */
export function primaryLocales(language: string): readonly Locale[] {
  const own = uiLocale(language);
  return own === SOURCE_LOCALE ? [SOURCE_LOCALE] : [SOURCE_LOCALE, own];
}

/** The locales an editor folds away, in registry order. */
export function otherLocales(language: string): readonly Locale[] {
  const primary = new Set(primaryLocales(language));
  return SUPPORTED_LOCALES.filter((l) => !primary.has(l));
}
